// The help centre's images: the screenshots imported from the Intercom Help Center (re-hosted under src/assets/help,
// so they're served from photohawk.com with the site's hashed, long-cached asset URLs), plus every guide image, plus
// the images staff upload in the platform editor (":::figure upload:<id>", served by the help API: helpImagesFor).
// scripts/help/import-intercom.mjs writes the files and ./images.json ({ id: { file, width, height } }); an article
// shows one with ":::figure help-<id>".

import { GUIDE_IMAGES, type GuideImage } from "@/content/guides/images";
import type { GuideBlock } from "@/content/guides/body";
import MANIFEST from "./images.json";
import { helpUploadUrl } from "./api";

const FILES = import.meta.glob("/src/assets/help/*.{png,jpg,jpeg,gif,webp,svg,avif}", { eager: true, import: "default" }) as Record<string, string>;

interface ManifestEntry {
  file: string;
  width: number;
  height: number;
}

const byFile = new Map(Object.entries(FILES).map(([path, url]) => [path.split("/").pop()!, url]));

/** Imported help images, by figure id ("help-<id>"). An entry whose file is missing is left out (the test reports it). */
export const HELP_ONLY_IMAGES: Record<string, GuideImage> = Object.fromEntries(
  Object.entries(MANIFEST as Record<string, ManifestEntry>).flatMap(([id, m]) => {
    const url = byFile.get(m.file);
    if (!url) return [];
    const img: GuideImage = { src: url, srcSet: `${url} ${m.width}w`, width: m.width, height: m.height };
    // Small screenshots stay at their own size instead of being blown up across the column.
    if (m.width < 680) img.maxWidth = m.width;
    return [[`help-${id}`, img]];
  }),
);

/** Every image a help article can show. */
export const HELP_IMAGES: Record<string, GuideImage> = { ...GUIDE_IMAGES, ...HELP_ONLY_IMAGES };

/** Manifest entries with no file in src/assets/help (for the tests). */
export const missingHelpImages = (): string[] =>
  Object.entries(MANIFEST as Record<string, ManifestEntry>)
    .filter(([, m]) => !byFile.has(m.file))
    .map(([id]) => id);

/** "upload:<random>-<width>x<height>.<ext>": an image uploaded in the platform editor (photohawk-serverless help store). */
const UPLOAD_RE = /^upload:([a-z0-9]{16,40})-(\d{1,5})x(\d{1,5})\.(png|jpg|gif|webp)$/;

/** An uploaded image's figure entry (served by the help API's public redirect), or undefined for any other id. */
export const uploadedHelpImage = (id: string, url: (imageId: string) => string = helpUploadUrl): GuideImage | undefined => {
  const m = UPLOAD_RE.exec(id);
  if (!m) return undefined;
  const imageId = id.slice("upload:".length);
  const src = url(imageId);
  const width = Number(m[2]);
  const height = Number(m[3]);
  const img: GuideImage = { src, srcSet: `${src} ${width}w`, width, height };
  if (width < 680) img.maxWidth = width;
  return img;
};

/** Every figure id some blocks use that's an upload. */
const uploadIds = (blocks: GuideBlock[]): string[] =>
  blocks.flatMap((b) => (b.type === "figure" && b.image.startsWith("upload:") ? [b.image] : b.type === "callout" ? uploadIds(b.blocks) : []));

/** The images an article can show: the bundled ones plus the uploads it uses. */
export const helpImagesFor = (blocks: GuideBlock[], url?: (imageId: string) => string): Record<string, GuideImage> => {
  const ids = uploadIds(blocks);
  if (!ids.length) return HELP_IMAGES;
  const extra: Record<string, GuideImage> = {};
  for (const id of ids) {
    const img = uploadedHelpImage(id, url);
    if (img) extra[id] = img;
  }
  return { ...HELP_IMAGES, ...extra };
};
